import { useState } from 'react';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';
import { caseStudies, type CaseStudy } from '../../../mocks/portfolio';

function FeaturedCard({ study }: { study: CaseStudy }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="relative grid lg:grid-cols-2 bg-ds-bg-2 border border-ds-border overflow-hidden reveal group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image side */}
      <div className="relative overflow-hidden h-72 lg:h-auto min-h-[340px]">
        {study.screenshotUrl ? (
          /* ── Laptop mockup ── */
          <div className="absolute inset-0 flex items-center justify-center bg-[#0d0d0d] px-8 py-6">
            {/* Ambient glow behind laptop */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 60%, rgba(0,180,180,0.12) 0%, transparent 70%)' }}
            />

            <div className="relative w-full max-w-[420px]">
              {/* ── Screen lid ── */}
              <div
                className="relative rounded-t-xl overflow-hidden"
                style={{
                  background: '#1c1c1e',
                  padding: '10px 10px 6px',
                  border: '1px solid #2e2e30',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 0 0 1px rgba(0,0,0,0.4)',
                }}
              >
                {/* Camera notch */}
                <div className="flex justify-center mb-2">
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: '#3a3a3c', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.9)' }}
                  />
                </div>
                {/* Screen — screenshot fills here */}
                <div
                  className={`overflow-hidden transition-transform duration-700 ${hovered ? 'scale-[1.02]' : 'scale-100'}`}
                  style={{ borderRadius: '2px', border: '1px solid #111' }}
                >
                  <img
                    src={study.screenshotUrl}
                    alt={`${study.client} website`}
                    className="w-full block"
                    style={{ display: 'block', maxHeight: '220px', objectFit: 'cover', objectPosition: 'top' }}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>

              {/* ── Hinge ── */}
              <div style={{ height: '4px', background: '#141416', borderLeft: '1px solid #2a2a2c', borderRight: '1px solid #2a2a2c' }} />

              {/* ── Base / keyboard deck ── */}
              <div
                style={{
                  height: '18px',
                  background: 'linear-gradient(to bottom, #1c1c1e, #161618)',
                  borderRadius: '0 0 8px 8px',
                  border: '1px solid #2a2a2c',
                  borderTop: 'none',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.9)',
                  position: 'relative',
                }}
              >
                {/* Trackpad indent */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '4px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '48px',
                    height: '6px',
                    background: '#222224',
                    borderRadius: '3px',
                  }}
                />
              </div>

              {/* ── Table/shadow under laptop ── */}
              <div
                style={{
                  height: '6px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)',
                  borderRadius: '0 0 50% 50%',
                  margin: '0 8px',
                }}
              />
            </div>
          </div>
        ) : (
          /* ── Standard photo ── */
          <>
            <img
              src={study.image}
              alt={study.client}
              className={`w-full h-full object-cover object-top transition-transform duration-700 ${hovered ? 'scale-105' : 'scale-100'}`}
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ds-bg-2/60" />
            <div className="absolute inset-0 bg-gradient-to-t from-ds-bg-2/80 via-transparent to-transparent lg:hidden" />
          </>
        )}

        {/* Industry badge — always shown */}
        <div className="absolute top-5 left-5 flex items-center gap-2 bg-ds-bg/80 border border-ds-red/30 px-3 py-1.5"
          style={{ backdropFilter: 'blur(8px)' }}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`${study.industryTag} text-ds-red text-xs`} />
          </div>
          <span className="font-body text-white/70 text-xs tracking-widest uppercase">{study.industry}</span>
        </div>
      </div>

      {/* Content side */}
      <div className="flex flex-col justify-center p-10 lg:p-12 relative">
        <div className="absolute top-0 left-0 bottom-0 w-px bg-gradient-to-b from-transparent via-ds-red/30 to-transparent hidden lg:block" />

        <span className="font-body text-ds-red text-xs tracking-[0.3em] uppercase mb-3 font-600">
          {study.featured ? 'Featured Project' : 'Case Study'}
        </span>
        <h3 className="font-heading font-900 text-white text-3xl lg:text-4xl leading-tight mb-3">
          {study.client}
        </h3>
        <p className="font-display text-ds-red text-xl mb-4" style={{ lineHeight: '1.2' }}>
          {study.headline}
        </p>
        <p className="font-body text-white/50 text-sm leading-relaxed mb-6">
          <strong className="text-white/70">Challenge:</strong> {study.challenge}
        </p>
        <p className="font-body text-white/50 text-sm leading-relaxed mb-8">
          <strong className="text-white/70">Solution:</strong> {study.solution}
        </p>

        {/* Results */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {study.results.map((r) => (
            <div key={r.label} className="bg-ds-bg border border-ds-border p-3 text-center">
              <div className="font-display text-2xl text-ds-red">{r.stat}</div>
              <div className="font-body text-white/40 text-xs mt-0.5 leading-tight">{r.label}</div>
            </div>
          ))}
        </div>

        {/* Service tags */}
        <div className="flex flex-wrap gap-2">
          {study.services.map((s) => (
            <span
              key={s}
              className="font-body text-xs text-white/50 border border-ds-border px-3 py-1"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PortfolioSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="work" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red/30 to-transparent" />
      <div className="absolute bottom-1/3 left-0 w-72 h-72 bg-ds-red/5 blur-[100px] rounded-full pointer-events-none" />

      <div
        className="max-w-7xl mx-auto px-6"
        ref={groupRef}
      >
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <span className="section-label reveal">Our Work</span>
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none reveal">
              PROOF OVER
              <br />
              <span className="text-ds-red neon-text-red">PROMISES.</span>
            </h2>
          </div>
          <p className="font-body text-white/50 text-sm max-w-xs leading-relaxed reveal">
            Real tourism businesses. Real results. Every project is a war won for an operator who was tired of losing bookings to aggregators and bigger names.
          </p>
        </div>

        {/* Tourism specialisation callout */}
        <div className="mb-8 reveal">
          <div
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-ds-red/25 bg-ds-red/5 px-7 py-5"
            style={{ borderLeft: '3px solid #B4FF39' }}
          >
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 flex items-center justify-center border border-ds-red/40 flex-shrink-0">
                <i className="ri-compass-3-line text-ds-red text-base" />
              </div>
              <div>
                <p className="font-display text-white text-sm tracking-wider leading-tight">
                  Tourism &amp; Hospitality Specialists
                </p>
                <p className="font-body text-white/45 text-xs mt-0.5 leading-relaxed">
                  Every client below is a tourism operator — reef trips, ferries, adventure charters. We know this industry.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-5 flex-shrink-0 pl-13 sm:pl-0">
              {['Reef &amp; Dive', 'Ferries', 'Adventures', 'Charters'].map((tag) => (
                <span key={tag} className="font-body text-white/35 text-xs tracking-widest uppercase hidden sm:block" dangerouslySetInnerHTML={{ __html: tag }} />
              ))}
            </div>
          </div>
        </div>

        {/* Case study cards */}
        <div className="space-y-6 mb-6">
          {caseStudies.map((study) => (
            <FeaturedCard key={study.id} study={study} />
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border border-ds-border p-6 reveal">
          <div>
            <p className="font-heading font-700 text-white text-lg">Want results like these?</p>
            <p className="font-body text-white/40 text-sm mt-0.5">Let&apos;s talk about what&apos;s possible for your business.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
